// src/services/classCompletion.js
// Per-viewer ABSORBING completion state. See docs/decisions/adr-class-completion-absorbing-state.md.
//
// Once a class is truly complete for THIS viewer, the live surface must never be
// re-mounted (re-entry hands off to On Demand). Before this, the per-viewer end
// lived only in component-local `classEndedLocally`, which resets on activeClass
// change — so during the ≤5-min cron-lag window (shared doc still says `live`)
// the viewer auto-activate could re-pull a finished viewer onto the live player,
// which autoplayed the sealed recording from the top ("the class started over").
//
// SAFETY PROPERTIES (load-bearing — see ADR invariants):
//   - READ-ONLY w.r.t. Firestore. This module NEVER writes shared state, so the
//     2026-05-29 cohort-cutoff mechanism (a client write fanned via onSnapshot)
//     structurally cannot recur. It only decides what THIS browser renders.
//   - Persisted to sessionStorage so a remount/reload/visibility cycle can't
//     resurrect a finished class. Monotonic add; the ONLY un-latch is the
//     admin-restart edge (ended/ending → live), handled by the caller.
//   - Degrades to today's behavior: if sessionStorage is unavailable the set is
//     empty (falls back to shared-state signal); if the server clock hasn't
//     synced, the trusted-clock signal is simply not evaluated.

import { serverNow, isServerClockSynced } from './serverClock';

const SS_KEY = 'mom_completed_classes_v1';

function readSet() {
    try {
        const raw = sessionStorage.getItem(SS_KEY);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? new Set(arr) : new Set();
    } catch (_) {
        // sessionStorage unavailable / blocked / malformed — degrade to empty.
        return new Set();
    }
}

function writeSet(set) {
    try {
        sessionStorage.setItem(SS_KEY, JSON.stringify([...set]));
    } catch (_) {
        // sessionStorage unavailable — silently degrade to today's behavior.
    }
}

// Latch a class id as completed-for-this-viewer. Monotonic — never removes.
export function markClassCompleted(classId) {
    if (!classId) return;
    const set = readSet();
    if (set.has(classId)) return;
    set.add(classId);
    writeSet(set);
}

// The ONE sanctioned un-latch: admin restart (the shared doc transitions
// ended/ending → live again on the same class). Detected by the caller.
export function clearClassCompleted(classId) {
    if (!classId) return;
    const set = readSet();
    if (!set.delete(classId)) return;
    writeSet(set);
}

export function isClassLatchedComplete(classId) {
    if (!classId) return false;
    return readSet().has(classId);
}

// Completion predicate for the live-surface activation gate. READ-ONLY.
// True ⇒ do NOT (re)mount the live surface for this viewer; route to On Demand.
//
// Signal 1 (the viewer's own Mux `ended` this session) is folded in via the
// latch — the caller marks it complete when `classEndedLocally` flips true.
// Signal 2 is the shared terminal state. Signal 3 is the trusted-clock past-end,
// gated on a synced clock and mirroring the existing pre-recorded end-time rule
// (prefer videoDurationSeconds, ignore when missing or < 30s).
export function isClassCompleteForViewer(cls) {
    if (!cls || !cls.id) return false;
    // Signal 1: previously latched (local Mux `ended`, or a prior eval of 2/3).
    if (isClassLatchedComplete(cls.id)) return true;
    // Signal 2: shared terminal state.
    const s = cls.state || cls.status;
    if (s === 'ended' || s === 'ending') return true;
    // Signal 3: trusted-clock past-end — pre-recorded only, synced clock only.
    if ((cls.classType || 'live') === 'prerecorded' && isServerClockSynced() && cls.scheduledAt) {
        const durSec = Number(cls.videoDurationSeconds);
        if (Number.isFinite(durSec) && durSec >= 30) {
            const startMs = new Date(cls.scheduledAt).getTime();
            if (Number.isFinite(startMs) && serverNow() >= startMs + durSec * 1000) return true;
        }
    }
    return false;
}
